/**
 * Excluded users data fetching and mutation hooks.
 *
 * NOTE: The Database type is missing Relationships fields on all tables,
 * so the Supabase typed client resolves all .from() data to `never`.
 * This is a codebase-wide issue — all hooks cast results explicitly.
 * Running `supabase gen types` would fix this globally.
 *
 * @module hooks/useExcludedUsers
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { ExcludedUser } from '../types';

/**
 * Parsed exclusion rules derived from the excluded_users table.
 */
export interface ExclusionRules {
  domains: string[];
  emails: string[];
}

/**
 * Fetches all excluded user rules.
 *
 * @returns Query result with excluded user rules
 */
export function useExcludedUsers() {
  return useQuery({
    queryKey: ['excluded-users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('excluded_users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch excluded users: ${error.message}`);
      }

      return (data || []) as unknown as ExcludedUser[];
    },
  });
}

/**
 * Fetches exclusion rules as parsed domain/email lists.
 * Used by dashboard stats and user filtering.
 *
 * TODO: useDashboardStats also fetches excluded_users inline. Consider sharing
 * query data via the 'exclusion-rules' cache key to avoid duplicate requests.
 *
 * @returns Query result with parsed exclusion rules
 */
export function useExclusionRules() {
  return useQuery({
    queryKey: ['exclusion-rules'],
    queryFn: async (): Promise<ExclusionRules> => {
      const { data, error } = await supabase
        .from('excluded_users')
        .select('*');

      if (error) {
        throw new Error(`Failed to fetch exclusion rules: ${error.message}`);
      }

      const rows = (data || []) as unknown as ExcludedUser[];
      const rules: ExclusionRules = { domains: [], emails: [] };
      for (const row of rows) {
        if (row.type === 'domain') rules.domains.push(row.value.toLowerCase());
        else if (row.type === 'email') rules.emails.push(row.value.toLowerCase());
      }
      return rules;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes — rules change infrequently
  });
}

/**
 * Adds a new excluded user rule.
 */
export function useAddExcludedUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ type, value }: { type: 'domain' | 'email'; value: string }) => {
      const { data, error } = await supabase
        .from('excluded_users')
        // Cast required: Database type is missing Relationships fields, so Insert resolves
        // to never. The shape matches ExcludedUserInsert. Fix by running `supabase gen types`.
        .insert({ type, value: value.toLowerCase().trim(), created_by: null } as never)
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error('This exclusion rule already exists');
        }
        throw new Error(`Failed to add exclusion: ${error.message}`);
      }

      return data as unknown as ExcludedUser;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['excluded-users'] });
      queryClient.invalidateQueries({ queryKey: ['exclusion-rules'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
    },
  });
}

/**
 * Removes an excluded user rule.
 */
export function useRemoveExcludedUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('excluded_users')
        .delete()
        .eq('id', id);

      if (error) {
        throw new Error(`Failed to remove exclusion: ${error.message}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['excluded-users'] });
      queryClient.invalidateQueries({ queryKey: ['exclusion-rules'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
    },
  });
}
